import * as Sentry from '@sentry/nextjs';
import UspSource from '../Data/UspSource';
import { incrementMetric } from '../Services/Datadog';
import { resolveDynamicUsps } from './supplierResolution';
import { getSelectAndConnectComponent } from './supplierMatchPredicates';

// Sentry groups on the message, so each source gets its own issue. The sources
// that are not a gap are absent deliberately: client-brands is the expected
// origin, and component-usps belongs to a card with no matched brand.
const SENTRY_MESSAGE_BY_SOURCE = {
  [UspSource.COMPONENT_GENERIC_USPS]: 'generic_usps_used',
  [UspSource.NONE]: 'usps_missing',
};

/**
 * Reports each rendered card whose USPs did not come from client brands, so the
 * registry entries still needing them can be found. Cards arrive already
 * resolved - the source a card rendered with is read, never worked out again.
 *
 * Reporting is per resolution, not per render - callers must guard against
 * re-reporting when their component re-renders.
 *
 * @param {{ dynamicSelectAndConnect: object, cards: object[], formId: string|number, subcategoryId: string }} args
 * @returns {void}
 */
export const reportWhereMissingUsps = ({
  dynamicSelectAndConnect,
  formId,
  subcategoryId,
  cards,
}) => {
  // Only a matched brand is expected to bring its own USPs. In a static journey
  // the configured copy is the intended content, not a gap.
  if (!dynamicSelectAndConnect?.enableDynamicSelectAndConnect) return;

  (cards || []).forEach((card) => {
    const message = SENTRY_MESSAGE_BY_SOURCE[card.uspsSource];

    if (!message) return;
    if (!card.brandId) return;

    // resolveSuppliers hands back cards keyed supplier_id; a raw match carries
    // supplierId.
    const supplierId = card.supplierId ?? card.supplier_id;

    incrementMetric({
      name: 'uspFallbacksUsed',
      tags: {
        form_id: formId,
        subcategory_id: subcategoryId,
        usp_source: card.uspsSource,
        brand_id: card.brandId,
        supplier_id: supplierId,
      },
    });

    Sentry.withScope((scope) => {
      scope.setTag('usp_source', card.uspsSource);
      scope.setTag('brand_id', String(card.brandId));
      scope.setTag('supplier_id', String(supplierId));
      scope.setTag('subcategory_id', String(subcategoryId));
      Sentry.captureMessage(message, 'warning');
    });
  });
};

/**
 * Reports the primary match's USP gap when it never reached a card. A dynamic
 * journey with exactly one match skips the Select and Connect page wholesale,
 * so neither SupplierCard nor SelectAndConnect mounts to report it - yet the
 * thankYouPageRequested event still hands that brand, USPs and all, to the
 * results page. With two or more matches the cards render and report
 * themselves, so this stays silent to avoid counting the primary twice.
 *
 * @param {{ dynamicSelectAndConnect: object, dynamicMatches: object[], components: object, formId: string|number, subcategoryId: string }} args
 * @returns {void}
 */
export const reportWhereMissingUspsForSkippedPrimary = ({
  dynamicSelectAndConnect,
  dynamicMatches,
  components,
  formId,
  subcategoryId,
}) => {
  if (dynamicMatches?.length !== 1) return;

  const [primary] = dynamicMatches;
  const genericUsps = getSelectAndConnectComponent({ components })?.component
    ?.additionalSettings?.generic_usps;
  const { source: uspsSource } = resolveDynamicUsps({
    matchedUsps: primary.usps,
    genericUsps,
  });

  reportWhereMissingUsps({
    dynamicSelectAndConnect,
    formId,
    subcategoryId,
    cards: [{ ...primary, uspsSource }],
  });
};
